import { atom, atomFamily, selector, selectorFamily, useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback, AtomEffect, CallbackInterface, DefaultValue } from 'recoil'
import { deleteDoc, deleteField, doc, collection, getDocs, query, where, onSnapshot, setDoc, updateDoc, DocumentData, writeBatch } from "firebase/firestore"

import {
	BENCHED,
  LOCAL_STORAGE_KEY_SETTINGS,
  NORMALIZE_MIN_QTRS_FOR_PAIRS,
  PAIR_MODES,
  PRESET_CUSTOM,
  ROSTER_NOT_AVAILABLE,
  SEASON_RULE_STANDARD,
  dbids,
  defaultNumPeriods,
  queryFieldPositions,
} from './definitions'
import { checkTeamFlowEntitlement, db, ensureFirebaseAuthUser } from './firebase'
import { deepCopy, findMyPositions, sanitizeBest, saveToLocalStorage, ucFirst } from './util'
import {
	findPairings,
	generateAllNetStats,
	initializeSettings,
} from './functions'
import { WhereFilterOp } from '@firebase/firestore-types'
import { PurchasesOfferings } from '@revenuecat/purchases-capacitor'
import { FLOW_DATA_SCHEMA_VERSION } from './flowSchema'
import { buildRosterAnalysis, deriveLegacySubs, normalizeEndRoster, requiresPersistedEndRoster, swapWholePositionTiles, validatedEndRoster } from './rosterAnalysis'

//
// syncWithFirebase()
//
// Fetches data from Firebase, listens for changes, and writes upon state change
//
type syncWithFirebaseOptions = {
	where?: [ string, WhereFilterOp, '__ID__' ]
	waitForServerOnEmptyCollection?: boolean
}

function hasActiveProEntitlement(info: StoreCustomerInfo | null | undefined) {
	if (!info) {
		return false
	}

	if (typeof info?.entitlements?.pro === 'object') {
		const { expires_date } = info.entitlements.pro
		if (!expires_date) {
			console.error('No expires_date in info.entitlements.pro')
			return false
		}
		return new Date(expires_date) > new Date()
	}

	if (!info?.entitlements?.active) {
		return false
	}

	// @ts-ignore
	if (info?.entitlements?.active?.pro) {
		console.error("Device-style with pro entitlement? Expected web API-style customer info.")
	}

	return false
}

const syncWithFirebase = <T>(opt: syncWithFirebaseOptions = { }): AtomEffect<T> => ({ node, setSelf, trigger }) => {

	const { key } = node

	const { name, id } = keyToId(key)

    // console.log('syncWithFirebase -> init', key, trigger, 'name', name, 'id', id, opt)

	if (!id || !name) {
		// Ignore bogus calls for data like team__NaN
        // console.log('syncWithFirebase - ignore bogus')
		return
	}

    let unsubscribe

	//
    // Load initial value from Firebase && setup listener
    //
	if (trigger === 'get') {

        // console.log('loadInitial()', key)

        const { isCollection, cid, docId } = metadata({
            name,
            id,
        })

        if (isCollection) {
            const collectionRef = collection(db, cid)

            let q

            if (opt?.where) {
				const [ fieldPath, opStr, value ] = opt.where
				const replacedValue = value.replace('__ID__', id)
				q = query(collectionRef, where(fieldPath, opStr, replacedValue))
			} else {
				q = query(collectionRef)
			}

			unsubscribe = onSnapshot(q, {
				includeMetadataChanges: opt.waitForServerOnEmptyCollection || false,
			}, querySnapshot => {

				if (opt.waitForServerOnEmptyCollection && querySnapshot.empty && querySnapshot.metadata.fromCache) {
					return
				}

				const data: {
					[key: string]: DocumentData
				} = { }

				// Insert 'id'
                querySnapshot.forEach(doc => {
					data[doc.id] = {
						...doc.data(),
						id: doc.id,
					}
				})

                // console.log('Collection data update', cid, data)

				setSelf(data as T)
            })

        } else {
            // console.log("Fetching", cid, docId, id)
            unsubscribe = onSnapshot(doc(db, cid, docId || id), (doc) => {
                const data = doc.data()

                // console.log('Document data update', cid, docId || id, data)

				// Insert 'id'
				setSelf({
					...data,
				   id,
				} as T)
			})
		}
	}

	return unsubscribe
}

async function saveRosterToFirebase(args: CallbackInterface, gameId: GameId, newState: Best) {

    const { snapshot } = args

    const s = await snapshot.getPromise(section)

    if (!s || !s.team || !s.season) {
        throw new Error("Missing team or season")
    }

    const { cid } = metadata({
        name: 'roster',
        id: `${s.team}__${s.season}`,
    })

    if (!cid) {
        throw new Error("No cid")
    }

	const numPeriods = await snapshot.getPromise(myNumPeriods)
	const gameFormat = await snapshot.getPromise(myGameFormat)
	const endRoster = normalizeEndRoster(newState.roster, newState.endRoster, newState.subs, numPeriods, gameFormat)
	const subs = deriveLegacySubs(newState.roster, endRoster, numPeriods, gameFormat)
	const needsEndRoster = requiresPersistedEndRoster(newState.roster, endRoster, subs, numPeriods, gameFormat)
	const created = Date.now()

    const data = {
        roster: newState.roster,
		endRoster: needsEndRoster ? endRoster : deleteField(),
		endRosterCreated: needsEndRoster ? created : deleteField(),
        players: Object.keys(newState.available).filter(player => newState.available[player]),
        orangeman: newState.orangeman,
        nulled: newState.nulled,
		subs,
		created,
    }

    console.log('saveRosterToFirebase() --->', cid, data)

    //
    // Create document if it doesn't already exist, otherwise merge in new data.
    //
    await setDoc(doc(db, cid, gameId), data, { merge: true })
}

export const saveRosterToFirebaseForTesting = saveRosterToFirebase

export const useSaveRoster = () => useRecoilCallback(args => (gameId: GameId, newState: Best) => saveRosterToFirebase(args, gameId, newState))

async function saveFlowToFirebase(args: CallbackInterface, gameId: GameId, newState: FlowData) {

	const { snapshot } = args

	const s = await snapshot.getPromise(section)
	const currentUser = await snapshot.getPromise(user)
	const existingFlow = await snapshot.getPromise(myFlow)
	const authUser = await ensureFirebaseAuthUser()

	if (currentUser?.uid && currentUser.uid !== authUser.uid) {
		throw Object.assign(new Error("App user and Firestore user do not match"), {
			code: 'flow-auth-mismatch',
		})
	}

	if (!s || !s.team || !s.season) {
		throw new Error("Missing team or season")
	}

	const { cid } = metadata({
		name: 'flow',
		id: `${s.team}__${s.season}`,
	})

	if (!cid) {
		throw new Error("No cid")
	}

	const now = Date.now()
	const currentTeam = await snapshot.getPromise(myTeam)
	const authUserIsManager = !!authUser.uid && !!currentTeam?.managers?.includes(authUser.uid)
	const existingAuthorUid = existingFlow?.authorUid || null
	const authorUid = existingAuthorUid && (existingAuthorUid === authUser.uid || authUserIsManager)
		? existingAuthorUid
		: authUser.uid
	const authorSessionId = existingFlow?.authorSessionId && authorUid === existingAuthorUid
		? existingFlow.authorSessionId
		: null
	const data: FlowData = {
		...newState,
		id: gameId,
		schemaVersion: newState.schemaVersion ?? existingFlow?.schemaVersion ?? FLOW_DATA_SCHEMA_VERSION,
		created: newState.created || now,
		updated: now,
		authorUid,
		authorSessionId,
		currentQuarter: newState.currentQuarter || 1,
		quarterBreaks: newState.quarterBreaks || [ ],
		events: newState.events || [ ],
		draftEvent: newState.draftEvent || null,
	}

	await setDoc(doc(db, cid, gameId), data, { merge: true })
}

export const useSaveFlow = () => useRecoilCallback(args => (gameId: GameId, newState: FlowData) => saveFlowToFirebase(args, gameId, newState), [ ])

async function deleteFlowFromFirebase(args: CallbackInterface, gameId: GameId) {

	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s || !s.team || !s.season) {
		throw new Error("Missing team or season")
	}

	const { cid } = metadata({
		name: 'flow',
		id: `${s.team}__${s.season}`,
	})

	if (!cid) {
		throw new Error("No cid")
	}

	await deleteDoc(doc(db, cid, gameId))

	try {
		await clearGameFlowMapFlagInFirebase(args, gameId)
	} catch (error) {
		console.warn('Deleted flow data but could not clear the game Flow Map flag.', error)
	}
}

export const useDeleteFlow = () => useRecoilCallback(args => (gameId: GameId) => deleteFlowFromFirebase(args, gameId), [ ])

async function clearGameFlowMapFlagInFirebase(args: CallbackInterface, gameId: GameId) {

	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s || !s.team || !s.season) {
		throw new Error("Missing team or season")
	}

	const { cid } = metadata({
		name: 'game',
		id: `${s.team}__${s.season}`,
	})

	if (!cid) {
		throw new Error("No cid")
	}

	await updateDoc(doc(db, cid, gameId), { hasFlowMap: false })
}

export const useClearGameFlowMapFlag = () => useRecoilCallback(args => (gameId: GameId) => clearGameFlowMapFlagInFirebase(args, gameId), [ ])

async function saveTrainingSessionToFirebase(
	args: CallbackInterface,
	dateId: string,
	date: number,
	attendance: { [key: PlayerId]: TrainingAttendanceState },
) {
	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s || !s.team || !s.season) {
		throw new Error("Missing team or season")
	}

	const { cid } = metadata({
		name: 'training',
		id: `${s.team}__${s.season}`,
	})

	if (!cid) {
		throw new Error("No cid")
	}

	const sessions = await snapshot.getPromise(myTrainingSessions)
	const existing = sessions?.[dateId]
	const now = Date.now()

	const data: TrainingSession = {
		id: dateId,
		date,
		attendance,
		created: existing?.created || now,
		updated: now,
	}

	await setDoc(doc(db, cid, dateId), data, { merge: true })
}

export const useSaveTrainingSession = () => useRecoilCallback(args => (dateId: string, date: number, attendance: { [key: PlayerId]: TrainingAttendanceState }) => saveTrainingSessionToFirebase(args, dateId, date, attendance))

async function saveTrainingNoteToFirebase(
	args: CallbackInterface,
	text: string,
) {
	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s || !s.team) {
		throw new Error("Missing team")
	}

	const { cid, docId } = metadata({
		name: 'trainingNote',
		id: `${s.team}__training`,
	})

	if (!cid || !docId) {
		throw new Error("No cid or docId")
	}

	const now = Date.now()
	const data: TrainingNote = {
		id: docId,
		text,
		updated: now,
	}

	await setDoc(doc(db, cid, docId), data, { merge: true })
}

export const useSaveTrainingNote = () => useRecoilCallback(args => (text: string) => saveTrainingNoteToFirebase(args, text))

async function deleteTrainingSessionFromFirebase(args: CallbackInterface, dateId: string) {
	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s || !s.team || !s.season) {
		throw new Error("Missing team or season")
	}

	const { cid } = metadata({
		name: 'training',
		id: `${s.team}__${s.season}`,
	})

	if (!cid) {
		throw new Error("No cid")
	}

	await deleteDoc(doc(db, cid, dateId))
}

export const useDeleteTrainingSession = () => useRecoilCallback(args => (dateId: string) => deleteTrainingSessionFromFirebase(args, dateId))

//
// Load & save 'settings' to/from localStorage
//

const saveLocalSettings = (): AtomEffect<Settings> => ({ onSet, setSelf, trigger }) => {

	// If there's a persisted value - set it on load
	const loadPersisted = async () => {
		const newValue = initializeSettings()
		console.log('loadPersisted()', newValue)
		setSelf(newValue)
	}

	if (trigger === 'get') {
		loadPersisted()
	}

	//
	// Subscribe to state changes and persist them to the db
	//
	const savePersisted = (newValue: Settings, oldValue: Settings | DefaultValue) => {
		console.log('savePersisted effect', newValue, oldValue)

		if (newValue && Object.keys(newValue).length) {
			// If we're using a standard setting, don't save anything except 'preset', 'showNetStats', 'showNetStatsCombos', 'playerPreferencesMode'
			const savedValues = (newValue.preset === PRESET_CUSTOM ? newValue : {
				preset: newValue.preset,
				showNetStats: newValue.showNetStats,
				showNetStatsCombos: newValue.showNetStatsCombos,
				preferenceType: newValue.preferenceType,
			})
			console.log("Saving to local storage", savedValues)
			saveToLocalStorage(LOCAL_STORAGE_KEY_SETTINGS, savedValues)
		}
	}

	onSet(savePersisted)
}


//
// user
//
const user = atom<User | null | undefined>({
	key: 'user',
	default: undefined,
})

export const useUser = () => useRecoilValue(user)
export const useSetUser = () => useSetRecoilState(user)
export const useUserState = () => useRecoilState(user)

//
// section
//
export const section = atom<Section>({
	key: 'section',
	default: { },
})

export const useSection = () => useRecoilValue(section)
export const useSetSection = () => useSetRecoilState(section)
export const useSectionState = () => useRecoilState(section)

const recent = atom<RecentTeams | null>({
	key: 'recent',
	default: null,
})

export const useRecent = () => useRecoilValue(recent)
export const useRecentState = () => useRecoilState(recent)

//
// newTeam
//
// Used when selecting a new team - we need to remember who they are
// for a second before we select a season.
//
export const newTeamId = atom<TeamId | null>({
	key: 'newTeamId',
	default: null,
})

export const useNewTeamId = () => useRecoilValue(newTeamId)
export const useNewTeamIdState = () => useRecoilState(newTeamId)
export const useSetNewTeamId = () => useSetRecoilState(newTeamId)

const amAddingNewSeason = atom<boolean>({
	key: 'amAddingNewSeason',
	default: false,
})

export const useAmAddingNewSeason = () => useRecoilValue(amAddingNewSeason)
export const useSetAmAddingNewSeason = () => useSetRecoilState(amAddingNewSeason)

//
// newSeasonConnector
//
export const newSeasonConnector = atom<Connector | null>({
	key: 'newSeasonConnector',
	default: null,
})

export const useNewSeasonConnectorState = () => useRecoilState(newSeasonConnector)

const myRelevantConnector = selector<Connector|null>({
	key: 'myRelevantConnector',
	get: ({ get }) => {
		if (get(newTeamId)) {
			return get(newSeasonConnector)
		}
		return get(mySeason)?.connector || null
	},
})

export const useMyRelevantConnector = () => useRecoilValue(myRelevantConnector)

const shouldSyncFixture = atom<'auto' | 'user' | false>({
	key: 'shouldSyncFixture',
	default: false,
})

export const useSetShouldSyncFixture = () => useSetRecoilState(shouldSyncFixture)
export const useShouldSyncFixtureState = () => useRecoilState(shouldSyncFixture)

//
// team
//
const team = atomFamily<Team | null, TeamId>({
    key: 'team',
    default: null,
    effects: [
        syncWithFirebase(),
    ],
})

export const useTeam = (teamId: TeamId) => useRecoilValue(team(teamId))

//
// myTeam
//
export const myTeam = selector<Team | null>({
    key: 'myTeam',
    get: ({ get }) => {
		const s = get(section)
		const teamId = s.team
		if (!teamId)
			return null
        return get(team(teamId))
    },
})

export const useMyTeam = () => useRecoilValue(myTeam)

const noTeamSelected = selector<boolean>({
	key: 'noTeamSelected',
	get: ({ get }) => {
		const currentSection = get(section)
		const currentTeam = get(myTeam)

		return !currentSection.team || (currentTeam !== null && !currentTeam.name)
	},
})

export const useNoTeamSelected = () => useRecoilValue(noTeamSelected)

//
// myRelevantTeamId
//
// If we're selecting a new team/season, returns that team, otherwise
// returns the currently loaded team.
//
// i.e. if 'newTeamId', then that, otherwise 'myTeam' (from 'section')
//
const myRelevantTeamId = selector<TeamId | null>({
	key: 'myRelevantTeamId',
	get: ({ get }) => {
		const tId = get(newTeamId)
		if (tId) {
			return tId
		}
		const s = get(section)
		return s?.team || null
	},
})

//
// myRelevantTeam
//
const myRelevantTeam = selector({
	key: 'myRelevantTeam',
	get: ({ get }) => {
		const teamId = get(myRelevantTeamId)
		if (!teamId)
			return null
		return get(team(teamId))
	}
})

export const useMyRelevantTeam = () => useRecoilValue(myRelevantTeam)

//
// userTeams
//
// Send uid of user, get an object of teams belonging to that user
//
const userTeams = atomFamily<Teams, UserId>({
    key: 'userTeams',
    default: { },
    effects: [
        syncWithFirebase({
            where: [ 'managers', 'array-contains', '__ID__' ],
        }),
    ],
})

export const useUserTeams = (uid: UserId) => useRecoilValue(userTeams(uid))

//
// myUserTeams
//
// Object of teams belonging to the current user
//
const myUserTeams = selector({
    key: 'myUserTeams',
    get: ({ get }) => {
        const u = get(user)
        if (!u)
            return null
        return get(userTeams(u.uid))
    },
})

export const useMyUserTeams = () => useRecoilValue(myUserTeams)

//
// userClubs
//
const userClubs = atomFamily<Clubs, ClubId>({
    key: 'userClubs',
    default: { },
    effects: [
        syncWithFirebase({
            where: [ 'users', 'array-contains', '__ID__' ],
        })
    ],
})

//
// clubs
//
const clubs = atomFamily<Clubs, ClubId>({
    key: 'clubs',
    effects: [
        syncWithFirebase()
    ],
})

//
// thisClub -- For loading another club not necessarily connected
// to the current user, specified by clubId.
//
const thisClub = selectorFamily<Club | null, ClubId>({
    key: 'thisClub',
    get: id => ({ get }) => {
        const obj = get(clubs(id))
		const club = obj[id]
        if (!club) {
            return null
        }
        return club
    },
})

export const useThisClub = (clubId: ClubId) => useRecoilValue(thisClub(clubId))

//
// club
//
const club = selector<Club | null>({
    key: 'club',
    get: ({ get }) => {
        const u = get(user)
        if (u) {
            const [ myClub ] = Object.values(get(userClubs(u.uid)))
			return myClub || null
        }
		return null
    },
})

export const useClub = () => useRecoilValue(club)

//
// clubUser
//
const clubUser = atomFamily<ClubUser, ClubUserId>({
    key: 'clubUser',
	default: undefined,
    effects: [
        syncWithFirebase(),
    ],
})

// export const useSetClubUser = (uid) => useSetRecoilState(clubUser(uid))

//
// myClubUser
//
const myClubUser = selectorFamily<ClubUser | null, UserId>({
    key: 'myClubUser',
	get: uid => ({ get }) => {
        const c = get(club)
		if (!c) {
			return null
		}
		return get(clubUser(`${c.id}__${uid}`))
	}
})

export const useMyClubUser = (uid: UserId) => useRecoilValue(myClubUser(uid))

const myClubUsersSorted = selector<Array<UserId>>({
	key: 'myClubUsersSorted',
	get: ({ get }) => {
		const c = get(club)
		if (!c) {
			return [ ]
		}
		const userLastLogins: {
			[key: string]: number
		} = { }
		c.users.forEach(uid => {
			const u = get(myClubUser(uid))
			userLastLogins[uid] = u?.lastLogin || 0
		})
		return c.users.slice().sort((a, b) => userLastLogins[b] - userLastLogins[a])
	},
})

export const useMyClubUsersSorted = () => useRecoilValue(myClubUsersSorted)

const FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT = 30

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
	const chunks: T[][] = []

	for (let i = 0; i < items.length; i += chunkSize) {
		chunks.push(items.slice(i, i + chunkSize))
	}

	return chunks
}

const syncClubTeamsChunk = (userIds: UserId[]): AtomEffect<Teams> => ({ setSelf, trigger }) => {
	if (trigger !== 'get' || !userIds.length) {
		return
	}

	const q = query(
		collection(db, dbids.team),
		where('managers', 'array-contains-any', userIds),
	)

	return onSnapshot(q, querySnapshot => {
		const teams: Teams = { }

		querySnapshot.forEach(doc => {
			teams[doc.id] = {
				...doc.data(),
				id: doc.id,
			} as Team
		})

		setSelf(teams)
	})
}

const clubTeamsChunk = atomFamily<Teams, UserId[]>({
	key: 'clubTeamsChunk',
	default: { },
	effects: userIds => [
		syncClubTeamsChunk(userIds),
	],
})

const myClubTeamChunks = selector<Array<UserId[]>>({
	key: 'myClubTeamChunks',
	get: ({ get }) => {
		const c = get(club)
		if (!c) {
			return [ ]
		}

		const uniqueSortedUsers = Array.from(new Set(c.users)).sort()
		return chunkArray(uniqueSortedUsers, FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT)
	},
})

const myClubTeams = selector<Teams>({
	key: 'myClubTeams',
	get: ({ get }) => {
		const teams: Teams = { }

		get(myClubTeamChunks).forEach(userIds => {
			Object.values(get(clubTeamsChunk(userIds))).forEach(team => {
				teams[team.id] = { ...team }
			})
		})

		return teams
	},
})

const myClubTeamsForUser = selectorFamily<Teams, UserId>({
	key: 'myClubTeamsForUser',
	get: uid => ({ get }) => {
		if (!uid) {
			return { }
		}

		const teams: Teams = { }

		Object.values(get(myClubTeams)).forEach(team => {
			if (team.managers?.includes(uid)) {
				teams[team.id] = { ...team }
			}
		})

		return teams
	},
})

export const useMyClubTeamsForUser = (uid: UserId) => useRecoilValue(myClubTeamsForUser(uid))

const myClubTeamNames = selector<ClubTeamNames>({
	key: 'clubTeams',
	get: ({ get }) => {

		return uniqueTeamNames(get(myClubTeams))
	},
})

export const useMyClubTeamNames = () => useRecoilValue(myClubTeamNames)

//
// Find teams that have different 'id' but same 'name' (ignoring
// leading/trailing whitespace and capitalization), and append
// '[v1]', '[v2]' etc to the name to differentiate them.
//
export function uniqueTeamNames(teams: Teams | Array<Team>): ClubTeamNames {
    const normalizeName = (name: string) => name.trim().toLocaleLowerCase()

    const displayName = (name: string) => name.trim()

    const countsByKey: Record<string, number> = { }
    const indexByKey: Record<string, number> = { }
    const teamNames: ClubTeamNames = { }

    const list = Object.values(teams)

    // Count by normalized key
    list.forEach(team => {
        const key = normalizeName(team.name)
        countsByKey[key] = (countsByKey[key] ?? 0) + 1
    })

    // Stable order: oldest created first
    list
        .slice()
        .sort((a, b) => a.created - b.created)
        .forEach(team => {
            const key = normalizeName(team.name)
            const base = displayName(team.name)

            if ((countsByKey[key] ?? 0) > 1) {
                indexByKey[key] = (indexByKey[key] ?? 0) + 1
                teamNames[team.id] = `${base} [${indexByKey[key]}]`
            } else {
                teamNames[team.id] = base
            }
        })

	return teamNames
}

//
// seasons
//
// Send teamId, get an object of all seasons belonging to this team
//
const teamSeasons = atomFamily<Seasons | null, TeamId>({
    key: 'teamSeasons',
    default: null,
    effects: [
        syncWithFirebase({
			waitForServerOnEmptyCollection: true,
		}),
    ],
})

export const useTeamSeasons = (teamId: TeamId) => useRecoilValue(teamSeasons(teamId))

/*
//
// mySeasons
//
// Get an object of all seasons for the current selected team
//
const mySeasons = selector({
    key: 'mySeasons',
    get: ({ get }) => {
        const { team: teamId } = get(section)
        return get(teamSeasons(teamId))
    }
})

export const useMySeasons = () => useRecoilValue(mySeasons)
*/

//
// mySeasonId
//
// Object of the current selected season for the current selected team
//
const mySeasonId = selector({
    key: 'mySeasonId',
    get: ({ get }) => {
        const { season: seasonId } = get(section)
        return seasonId
    }
})

export const useMySeasonId = () => useRecoilValue(mySeasonId)

//
// mySeason
//
// Object of the current selected season for the current selected team
//
const mySeason = selector<Season | null>({
    key: 'mySeason',
    get: ({ get }) => {
        const { team: teamId, season: seasonId } = get(section)
		if (teamId && seasonId) {
			const ts = get(teamSeasons(teamId))
			return ts?.[seasonId] || null
		}
		return null
	},
})

export const useMySeason = () => useRecoilValue(mySeason)

const haveValidSeason = selector<boolean>({
	key: 'haveValidSeason',
	get: ({ get }) => {
		const s = get(mySeason)
		if (s && Object.keys(s).length) {
			return true
		}
		return false
	},
})

export const useHaveValidSeason = () => useRecoilValue(haveValidSeason)

const mySeasonStatus = selector<string>({
	key: 'amLoadingSeason',
	get: ({ get }) => {
		const { team: teamId, season: seasonId } = get(section)
		if (teamId && seasonId) {
			const ts = get(teamSeasons(teamId))
			if (ts === null) {
				return 'loading'
			}
			if (ts?.[seasonId]) {
			   return 'loaded'
			}
			return 'no-season'
		}
		return 'no-seasonid'
	}
})

export const useMySeasonStatus = () => useRecoilValue(mySeasonStatus)

//
// myNumPeriods
//
// For the current season, the number of periods per game
//
const myNumPeriods = selector({
	key: 'numPeriods',
	get: ({ get }) => {
		const s = get(mySeason)
		return s?.numPeriods || defaultNumPeriods
	},
})

export const useMyNumPeriods = () => useRecoilValue(myNumPeriods)

//
// myNumPeriodsLoaded
//
// Like 'myNumPeriods', but will return 'null' rather than '4' if
// we haven't loaded this value yet.
//
const myNumPeriodsLoaded = selector({
	key: 'numPeriodsLoaded',
	get: ({ get }) => {
		const s = get(mySeason)
		if (!s)
			return null
		return s.numPeriods || defaultNumPeriods
	},
})

export const useMyNumPeriodsLoaded = () => useRecoilValue(myNumPeriodsLoaded)

const myGameFormat = selector({
	key: 'myGameFormat',
	get: ({ get }) => {
		const s = get(mySeason)
		return s?.gameFormat || 0
	},
})

export const useMyGameFormat = () => useRecoilValue(myGameFormat)

const mySeasonRules = selector<SeasonRules>({
	key: 'mySeasonRules',
	get: ({ get }) => {
		const s = get(mySeason)
		return s?.rules ?? SEASON_RULE_STANDARD
	},
})

export const useMySeasonRules = () => useRecoilValue(mySeasonRules)

//
// myFieldPositions
//
// For the current season, the fieldPositions
//
const myFieldPositions = selector({
	key: 'myFieldPositions',
	get: ({ get }) => {
		const f = get(myGameFormat)
		return queryFieldPositions(f || 0)
	},
})

export const useMyFieldPositions = () => useRecoilValue(myFieldPositions)

//
// games
//
const teamGames = atomFamily<Games | null, TeamSeasonId>({
    key: 'teamGames',
    default: null,
    effects: [
        syncWithFirebase(),
    ],
})

//
// training sessions
//
const teamTrainingSessions = atomFamily<TrainingSessions | null, TeamSeasonId>({
	key: 'teamTrainingSessions',
	default: null,
	effects: [
		syncWithFirebase(),
	],
})

const myTrainingSessions = selector<TrainingSessions | null>({
	key: 'myTrainingSessions',
	get: ({ get }) => {
		const { team: teamId, season: seasonId } = get(section)
		if (!teamId || !seasonId) {
			return null
		}
		const id = `${teamId}__${seasonId}`
		return get(teamTrainingSessions(id))
	},
})

export const useMyTrainingSessions = () => useRecoilValue(myTrainingSessions)

//
// training note (team-level)
//
const trainingNote = atomFamily<TrainingNote | null, string>({
	key: 'trainingNote',
	default: null,
	effects: [
		syncWithFirebase(),
	],
})

const myTrainingNote = selector<TrainingNote | null>({
	key: 'myTrainingNote',
	get: ({ get }) => {
		const { team: teamId } = get(section)
		if (!teamId) {
			return null
		}
		return get(trainingNote(`${teamId}__training`))
	},
})

export const useMyTrainingNote = () => useRecoilValue(myTrainingNote)

//
// myGames
//
// Get an object of all games for the current selected team & season
//
export const myGames = selector({
    key: 'myGames',
    get: ({ get }) => {
        const { team: teamId, season: seasonId } = get(section)
		if (!teamId || !seasonId) {
			return null
		}
        const id = `${teamId}__${seasonId}`
        return get(teamGames(id))
    }
})

export const useMyGames = () => useRecoilValue(myGames)

const myGame = selector<Game | null>({
    key: 'myGame',
    get: ({ get }) => {
        const g = get(myGames)
        if (!g)
            return null
        const gameId = get(myGameId)
		if (!gameId)
			return null
        return g[gameId] || null
    },
})

export const useMyGame = () => useRecoilValue(myGame)

const myGameId = selector({
    key: 'myGameId',
    get: ({ get }) => {
        const { game: gameId } = get(section)
        return gameId
    },
})

export const useMyGameId = () => useRecoilValue(myGameId)

//
// players
//
export const teamPlayers = atomFamily<Players | null, TeamSeasonId>({
    key: 'teamPlayers',
    default: null,
    effects: [
        syncWithFirebase(),
    ],
})

//
// myPlayers
//
// Get an object of all games for the current selected team & season
//
const myPlayers = selector<Players | null>({
    key: 'myPlayers',
    get: ({ get }) => {
        const { team: teamId, season: seasonId } = get(section)
		if (!teamId || !seasonId) {
			return null
		}
        const id = `${teamId}__${seasonId}`
        const r = get(teamPlayers(id))
        return r
    }
})

export const useMyPlayers = () => useRecoilValue(myPlayers)

//
// myRegularPlayers
//
// Like 'myPlayers' but excludes fillins.
//
const myRegularPlayers = selector<Players | null>({
	key: 'myRegularPlayers',
	get: ({ get }) => {
		const p = get(myPlayers)
		if (!p)
			return null
		const regularPlayers: Players = { }
		Object.keys(p).forEach(playerId => {
			if (!p[playerId].isFillin) {
				regularPlayers[playerId] = p[playerId]
			}
		})
		return regularPlayers
	},
})

export const useMyRegularPlayers = () => useRecoilValue(myRegularPlayers)

const myFillins = selector<Players | null>({
	key: 'myFillins',
	get: ({ get }) => {
		const p = get(myPlayers)
		if (!p)
			return null
		const fillins: Players = { }
		Object.keys(p).forEach(playerId => {
			if (p[playerId].isFillin) {
				fillins[playerId] = p[playerId]
			}
		})
		return fillins
	},
})

export const useMyFillins = () => useRecoilValue(myFillins)

//
// myPlayer
//
// Get a single player object
//
const myPlayer = selectorFamily<Player | null, PlayerId>({
    key: 'myPlayer',
    get: playerId => ({ get }) => {
        const p = get(myPlayers)
        if (!p)
            return null
        return p[playerId] || null
    },
})

export const useMyPlayer = (playerId: PlayerId) => useRecoilValue(myPlayer(playerId))

//
// myPlayerId
//
// Extracted from 'section'
//
const myPlayerId = selector({
    key: 'myPlayerId',
    get: ({ get }) => {
        const { player: playerId } = get(section)
        return playerId
    },
})

export const useMyPlayerId = () => useRecoilValue(myPlayerId)

//
// myPlayerNames
//
// This has an optimization to prevent unncessary re-renders. We
// use the stringified function as a stopper to prevent a flow
// of re-rendering.
//
const myPlayerNamesStringified = selector<string|null>({
    key: 'myPlayerNamesStringified',
    get: ({ get }) => {
        const p = get(myPlayers)
        if (!p)
            return null
		const playerNames: {
			[key: string]: string
		} = { }
	   	Object.keys(p).forEach(playerId => {
            playerNames[playerId] = p[playerId].name
        })
        return JSON.stringify(playerNames)
    },
})

const myPlayerNames = selector({
    key: 'myPlayerNames',
    get: ({ get }) => {
        const json = get(myPlayerNamesStringified)
        if (!json)
            return null
        return JSON.parse(json)
    },
})

export const useMyPlayerNames = () => useRecoilValue(myPlayerNames)

const myPlayerName = selectorFamily<string, PlayerId>({
    key: 'myPlayerName',
    get: playerId => ({ get }) => {
        const p = get(myPlayerNames)
        if (!p)
            return null
        return p[playerId]
    },
})

export const useMyPlayerName = (playerId: PlayerId) => useRecoilValue(myPlayerName(playerId))

//
// Can be better than calling Object.keys(myPlayers) because this
// only updates when id or name changes.
//
const myPlayerIds = selector<PlayerId[]>({
    key: 'myPlayerIds',
    get: ({ get }) => {
        const pn = get(myPlayerNames)
        if (!pn)
            return pn
        return Object.keys(pn)
    },
})

export const useMyPlayerIds = () => useRecoilValue(myPlayerIds)

const myPlayerIsFillIn = selectorFamily<boolean, PlayerId>({
	key: 'myPlayerIsFillIn',
	get: playerId => ({ get }) => {
		const p = get(myPlayer(playerId))
		return p?.isFillin || false
	},
})

export const useMyPlayerIsFillIn = (playerId: PlayerId) => useRecoilValue(myPlayerIsFillIn(playerId))

const myPlayerIsMale = selectorFamily<boolean, PlayerId>({
	key: 'myPlayerIsMale',
	get: playerId => ({ get }) => {
		const p = get(myPlayer(playerId))
		return p?.isMale || false
	},
})

export const useMyPlayerIsMale = (playerId: PlayerId) => useRecoilValue(myPlayerIsMale(playerId))

type FlowDocumentId = {
	teamId: TeamId
	seasonId: SeasonId
	gameId: GameId
}

type FlowDocumentState = {
	loaded: boolean
	data: FlowData | null
}

const gameFlow = atomFamily<FlowDocumentState, FlowDocumentId>({
	key: 'gameFlow',
	default: {
		loaded: false,
		data: null,
	},
	effects: ({ teamId, seasonId, gameId }) => [
		({ setSelf, trigger }) => {
			if (trigger !== 'get') {
				return
			}

			return onSnapshot(
				doc(db, `/teams/${teamId}/seasons/${seasonId}/flows`, gameId),
				snapshot => {
					setSelf({
						loaded: true,
						data: snapshot.exists() ? {
							...snapshot.data(),
							id: snapshot.id,
						} as FlowData
							: null,
					})
				},
			)
		},
	],
})

const myFlowForGame = selectorFamily<FlowDocumentState, GameId | null | undefined>({
	key: 'myFlowForGame',
	get: gameId => ({ get }) => {
		const { team: teamId, season: seasonId } = get(section)
		if (!teamId || !seasonId || !gameId) {
			return {
				loaded: true,
				data: null,
			}
		}

		return get(gameFlow({ teamId, seasonId, gameId }))
	},
})

export const useMyFlowForGame = (gameId: GameId | null | undefined) => useRecoilValue(myFlowForGame(gameId))

const myFlow = selector<FlowData | null>({
	key: 'myFlow',
	get: ({ get }) => {
		const { team: teamId, season: seasonId, game: gameId } = get(section)
		if (!teamId || !seasonId || !gameId) {
			return null
		}
		return get(gameFlow({ teamId, seasonId, gameId })).data
	},
})

export const useMyFlow = () => useRecoilValue(myFlow)

const myFlowIsLoading = selector<boolean>({
	key: 'myFlowIsLoading',
	get: ({ get }) => {
		const { team: teamId, season: seasonId, game: gameId } = get(section)
		if (!teamId || !seasonId || !gameId) {
			return false
		}
		return !get(gameFlow({ teamId, seasonId, gameId })).loaded
	},
})

export const useMyFlowIsLoading = () => useRecoilValue(myFlowIsLoading)

//
// teamRosters
//
const teamRosters = atomFamily<Rosters, TeamSeasonId>({
    key: 'teamRosters',
    default: { },
    effects: [
        syncWithFirebase(),
    ],
})

export const useTeamRosters = (id: TeamSeasonId) => useRecoilValue(teamRosters(id))

//
// myRosters
//
// Get an object of all games for the current selected team & season
//
export const myRosters = selector({
    key: 'myRosters',
    get: ({ get }) => {
        const { team: teamId, season: seasonId } = get(section)
		if (!teamId || !seasonId) {
			return { }
		}
        const id = `${teamId}__${seasonId}`
        return get(teamRosters(id))
    }
})

export const useMyRosters = () => useRecoilValue(myRosters)

//
// myRoster
//
const myRoster = selector<Roster | null>({
    key: 'myRoster',
    get: ({ get }) => {
        const r = get(myRosters)
        const { game: gameId } = get(section)
		if (!gameId)
			return null
        return r[gameId] || null
    },
})

export const useMyRoster = () => useRecoilValue(myRoster)

//
// myOnCourtRosters - returns a roster array but with 'null' if
// the scheduled player was unavailable or nulled, and with each
// position being an array of players who played there.
//
// Used only by findPairings().
//
const myOnCourtRosters = selector({
    key: 'myOnCourtRosters',
    get: ({ get }) => {
        const r = get(myRosters)
		const obj: OnCourtRosters = { }
        Object.keys(r).forEach(gameId => {

			const { roster, endRoster, endRosterCreated, created, players, nulled, subs } = r[gameId]

			const availablePlayers: {
				[key: string]: boolean
			} = { }

			players.forEach(playerId => {
				availablePlayers[playerId] = true
			})

			const onCourtRoster = buildRosterAnalysis({
				roster,
				endRoster: validatedEndRoster(endRoster, created, endRosterCreated),
				subs,
				players,
				numPeriods: get(myNumPeriods),
				gameFormat: get(myGameFormat),
			}).onCourtRoster.map(halves => halves.map(playerId => (playerId && availablePlayers[playerId]) ? playerId : null))

            nulled?.forEach(positionIndex => onCourtRoster[positionIndex] = [ null ])

            obj[gameId] = onCourtRoster
        })
		return obj
    }
})

export const useMyOnCourtRosters = () => useRecoilValue(myOnCourtRosters)

const amDragging = atom({
	key: 'amDragging',
	default: false,
})

export const useAmDragging = () => useRecoilValue(amDragging)
export const useSetAmDragging = () => useSetRecoilState(amDragging)

//
// best - A local copy of key data, because we don't want to wait
// for a round trip to Firebase to update what the user is seeing.
//
function defaultBest(): Best {
	return {
		created: 0,
		roster: [ ],
		endRoster: [ ],
		endRosterCreated: 0,
		available: { },
		players: [ ],
		nulled: [ ],
		subs: [ ],
		orangeman: null,
	}
}

const best = atomFamily<Best, GameId>({
    key: 'best',
    default: defaultBest(),
})

const myBest = selector<Best>({
    key: 'myBest',
    get: ({ get }) => {
        const id = get(myGameId)
		if (id) {
			const ret = get(best(id))
			if (ret) {
				return ret
			}
		}
        return defaultBest()
    },
    set: ({ get, set }, newValue) => {
        const id = get(myGameId)
        if (id) {
            return set(best(id), newValue)
        }
    },
})

export const useMyBest = () => useRecoilValue(myBest)
export const useMyBestState = () => useRecoilState(myBest)

const madeChanges = atomFamily({
    key: 'madeChanges',
    default: false,
})

const myMadeChanges = selector({
    key: 'myMadeChanges',
    get: ({ get }) => {
        const id = get(myGameId)
        return get(madeChanges(id)) || false
    },
    set: ({ get, set }, newValue) => {
        const id = get(myGameId)
        return set(madeChanges(id), newValue)
    },
})

export const useSetMyMadeChanges = () => useSetRecoilState(myMadeChanges)
export const useMyMadeChangesState = () => useRecoilState(myMadeChanges)

//
// locks
//
const locks = atomFamily<Array<boolean>, GameId>({
	key: 'locks',
	default: [ ],
})

const myLocks = selector({
	key: 'myLocks',
	get: ({ get }) => {
		const id = get(myGameId) || ''
		return get(locks(id)) || [ ]
	},
	set: ({ get, set }, newValue) => {
		const id = get(myGameId) || ''
		return set(locks(id), newValue)
	},
})

export const useMyLocks = () => useRecoilValue(myLocks)
export const useMyLocksState = () => useRecoilState(myLocks)

//
// History
//
const rosterHistory = atomFamily<Array<Best>, GameId>({
    key: 'rosterHistory',
    default: [ ],
})

const rosterHistoryIndex = atomFamily({
    key: 'rosterHistoryIndex',
    default: 0,
})

const canUndo = selector({
    key: 'canUndo',
    get: ({ get }) => {
        if (!get(ready)) {
            return false
        }
        const id = get(myGameId)
        const i = get(rosterHistoryIndex(id))
        return i > 1
    },
})

export const useCanUndo = () => useRecoilValue(canUndo)

const canRedo = selector({
    key: 'canRedo',
    get: ({ get }) => {
        if (!get(ready)) {
            return false
        }
        const id = get(myGameId)
		if (!id)
			return false
        const history = get(rosterHistory(id))
        const i = get(rosterHistoryIndex(id))
        return i < history.length
    },
})

export const useCanRedo = () => useRecoilValue(canRedo)

async function addToHistory(args: CallbackInterface, newState?: Best, isNewHistory?: boolean, isErase?: boolean) {

    const { snapshot, set } = args

    const id = await snapshot.getPromise(myGameId)

	if (!id)
		return

    const stateToAdd = newState || await snapshot.getPromise(best(id))
    const currentBestHistory = await snapshot.getPromise(rosterHistory(id))
    const currentBestHistoryIndex = await snapshot.getPromise(rosterHistoryIndex(id))

	//
	// Every time new data is received by Roster::calculate_initial_values(), it sends
	// a request here via startNewHistory(). This is often triggered by autosave, so
	// we want to ignore it. However, if we're loading the roster for the first time,
	// we probably got sent an empty roster before it was fully loaded, and we want
	// to let that be overwritten by the actual data.
	//
	// If the user is erasing a roster, though, let's record that as valid history.
	//
	if (isNewHistory && !isErase) {
		if (newState && currentBestHistory.length === 1 && currentBestHistory[0].roster.length === 0) {
			//
			// This is the correctly loaded initial state, which should be allowed
			// to overwrite the single existing history.
			//
			set(rosterHistory(id), [ newState ])
		}
		if (currentBestHistory.length) {
			// console.log("Not adding initial history because we already have some.")
			return
		}
	}

    const newBestHistory = currentBestHistory.slice(0, currentBestHistoryIndex)
    newBestHistory.push(stateToAdd)

    const newBestHistoryIndex = newBestHistory.length

    set(rosterHistory(id), newBestHistory)

    set(rosterHistoryIndex(id), newBestHistoryIndex)
}

export const useAddToHistory = () => useRecoilCallback(args => (newState?: Best) => addToHistory(args, newState))
export const useStartHistory = () => useRecoilCallback(args => (newState: Best, isErase?: boolean) => addToHistory(args, newState, true, isErase), [ ]) // This caching is important to prevent infinite loops in Roster.js -> calculateInitialValues()

async function undo(args: CallbackInterface, undoAll?: boolean) {
    const { snapshot, set } = args

    const id = await snapshot.getPromise(myGameId)
	if (!id)
		return

    const currentBestHistory = await snapshot.getPromise(rosterHistory(id))
    const currentBestHistoryIndex = await snapshot.getPromise(rosterHistoryIndex(id))

    if (currentBestHistoryIndex > 1) {
        if (undoAll) {
            set(best(id), currentBestHistory[0])
            set(rosterHistoryIndex(id), 1)
        } else {
            set(best(id), currentBestHistory[currentBestHistoryIndex - 2])
            set(rosterHistoryIndex(id), currentBestHistoryIndex - 1)
        }
        set(madeChanges(id), true)
    }
}

export const useUndo = () => useRecoilCallback(args => () => undo(args), [ ])
export const useUndoAll = () => useRecoilCallback(args => () => undo(args, true), [ ])

async function redo(args: CallbackInterface, redoAll?: boolean) {
    const { snapshot, set } = args

    const id = await snapshot.getPromise(myGameId)
	if (!id)
		return

	const currentBestHistory = await snapshot.getPromise(rosterHistory(id))
    const currentBestHistoryIndex = await snapshot.getPromise(rosterHistoryIndex(id))

    if (currentBestHistoryIndex < currentBestHistory.length) {
        if (redoAll) {
            set(best(id), currentBestHistory[currentBestHistory.length - 1])
            set(rosterHistoryIndex(id), currentBestHistory.length)
        } else {
            set(best(id), currentBestHistory[currentBestHistoryIndex])
            set(rosterHistoryIndex(id), currentBestHistoryIndex + 1)
        }
        set(madeChanges(id), true)
    }
}

export const useRedo = () => useRecoilCallback(args => () => redo(args))
export const useRedoAll = () => useRecoilCallback(args => () => redo(args, true), [ ])

const helpTab = atom({
	key: 'helpTab',
	default: '',
})

export const useHelpTabState = () => useRecoilState(helpTab)
export const useSetHelpTab = () => useSetRecoilState(helpTab)

async function swapPositions(args: CallbackInterface, id: GameId, from: RosterPositionIndex, to: RosterPositionIndex) {

    const { set, snapshot } = args

    console.log('swapPositions', from, to)

	if (typeof from !== 'number' || typeof to !== 'number' || isNaN(from) || isNaN(to)) {
		console.error("Refusing to perform swap between non-numerical positions", from, to)
		return
	}

	const numPeriods = await snapshot.getPromise(myNumPeriods)
	const gameFormat = await snapshot.getPromise(myGameFormat)

    set(best(id), prevState => {
        const newState = deepCopy(prevState)

		// Sanity check: Make sure from/to quarters match
		const playersPerQtr = newState.roster.length / numPeriods
		if (Math.floor(from / playersPerQtr) !== Math.floor(to / playersPerQtr)) {
			console.error("Illegal attempt to swap", from, to, "with roster", newState)
			return prevState
		}

		const endRoster = normalizeEndRoster(prevState.roster, prevState.endRoster, prevState.subs, numPeriods, gameFormat)
		const swapped = swapWholePositionTiles(prevState.roster, endRoster, from, to, numPeriods)
		newState.roster = swapped.roster
		newState.endRoster = swapped.endRoster
        // console.log('new best', newState.roster)

        void addToHistory(args, newState)

        return newState
    })

    set(madeChanges(id), true)
}

export const useSwapPositions = (id: GameId) => useRecoilCallback(args => (from: RosterPositionIndex, to: RosterPositionIndex) => swapPositions(args, id, from, to))

async function swapEndPositions(args: CallbackInterface, id: GameId, from: RosterPositionIndex, to: RosterPositionIndex) {
	const { snapshot, set } = args
	const prevState = await snapshot.getPromise(best(id))
	const numPeriods = await snapshot.getPromise(myNumPeriods)
	const gameFormat = await snapshot.getPromise(myGameFormat)
	const playersPerQtr = prevState.roster.length / numPeriods

	if (
		!Number.isInteger(from) ||
		!Number.isInteger(to) ||
		from === to ||
		Math.floor(from / playersPerQtr) !== Math.floor(to / playersPerQtr)
	) {
		return
	}

	const endRoster = normalizeEndRoster(prevState.roster, prevState.endRoster, prevState.subs, numPeriods, gameFormat)
	;[ endRoster[from], endRoster[to] ] = [ endRoster[to], endRoster[from] ]
	const newState = sanitizeBest({ ...prevState, endRoster }, numPeriods, gameFormat)
	set(best(id), newState)
	await addToHistory(args, newState)
	set(madeChanges(id), true)
}

export const useSwapEndPositions = (id: GameId) => useRecoilCallback(args => (
	from: RosterPositionIndex,
	to: RosterPositionIndex,
) => swapEndPositions(args, id, from, to))

function swapPlayers(args: CallbackInterface, id: GameId, from: PlayerId, to: PlayerId) {

	const { set } = args

	console.log('swapPlayers', from, to)

	if (typeof from !== 'string' || typeof to !== 'string') {
		console.error("Refusing to perform swap between non-string players", from, to)
		return
	}

	const swapPlayer = (player: PlayerId) => {
		if (player === from)
			return to
		else if (player === to)
			return from
		return player
	}

	set(best(id), prevState => {
		const newState = deepCopy(prevState)

		newState.players = newState.players.map(player => swapPlayer(player))
		newState.roster = newState.roster.map(player => swapPlayer(player))
		newState.endRoster = newState.endRoster?.map(player => swapPlayer(player))

		void addToHistory(args, newState)

		return newState
	})

	set(madeChanges(id), true)
}

export const useSwapPlayers = (id: GameId) => useRecoilCallback(args => (from: PlayerId, to: PlayerId) => swapPlayers(args, id, from, to))

async function swapQtrs(args: CallbackInterface, id: GameId, from: number, to: number) {

    const { snapshot, set } = args

	console.log('swapQtrs', from, '-->', to)

	const b = await snapshot.getPromise(best(id))
	const n = await snapshot.getPromise(myNumPeriods)
	const gameFormat = await snapshot.getPromise(myGameFormat)

	const qtrLength = b.roster.length / n

    set(best(id), prevState => {
        const newState = deepCopy(prevState)
		const { roster } = prevState
		const endRoster = normalizeEndRoster(prevState.roster, prevState.endRoster, prevState.subs, n, gameFormat)
		newState.endRoster = endRoster.slice()

		for (let i = 0; i < qtrLength; i += 1) {
			const fromI = from * qtrLength + i
			const toI = to * qtrLength + i

			newState.roster[fromI] = roster[toI]
			newState.roster[toI] = roster[fromI]
			newState.endRoster[fromI] = endRoster[toI]
			newState.endRoster[toI] = endRoster[fromI]
		}

		//
		// Fix-up 'nulled' and 'subs'
		//
		const fields = [ 'nulled', 'subs' ] as const
		fields.forEach(field => {
			const posIndexes = prevState[field]
			if (posIndexes?.length) {
				const newPosIndexes = posIndexes.map(i => {
					if (i >= from * qtrLength && i < (from + 1) * qtrLength) {
						return to * qtrLength + (i % qtrLength)
					} else if (i >= to * qtrLength && i < (to + 1) * qtrLength) {
						return from * qtrLength + (i % qtrLength)
					}
					return i
				})
				console.log("Mapping", field, posIndexes, "to", newPosIndexes, 'with', from, to)
				newState[field] = newPosIndexes
			}
		})

        void addToHistory(args, newState)

        return newState
    })

	set(locks(id), prevState => {
		const newState = prevState.slice()

		for (let i = 0; i < qtrLength; i += 1) {
			const fromI = from * qtrLength + i
			const toI = to * qtrLength + i

			newState[fromI] = prevState[toI]
			newState[toI] = prevState[fromI]
		}

		return newState
	})

    set(madeChanges(id), true)
}

export const useSwapQtrs = (id: GameId) => useRecoilCallback(args => (from: number, to: number) => swapQtrs(args, id, from, to))

//
// Respond to the user tapping a position. This will cycle through available states:
// 1. Normal
// 2. Locked
// 3. Nulled (if available)
//
async function toggleLock(args: CallbackInterface, id: GameId, i: RosterPositionIndex) {

	const { snapshot } = args

	const b = await snapshot.getPromise(best(id))
	const l = await snapshot.getPromise(locks(id))

	const isLocked = l[i]
	const isNulled = b.nulled.includes(i)
	const currentState = isLocked ? 'locked' : isNulled ? 'nulled' : 'normal'
	const { date } = await snapshot.getPromise(myGame) || { }
	const canNull = !!(date && date < Date.now())

	const states = [ 'normal', 'locked' ]
	if (canNull) {
		states.push('nulled')
	}

	const currentIndex = states.indexOf(currentState)
	const nextIndex = (currentIndex + 1) % states.length
	const nextState = states[nextIndex]

	console.log('Toggling lock', i, 'from', currentState, 'to', nextState)

	switch (nextState) {
		case 'locked': {
			await setLock(args, id, i, true)
			break
		}
		case 'nulled': {
			await setLock(args, id, i, false)
			await setNulled(args, id, i, true)
			break
		}
		default: {
			await setLock(args, id, i, false)
			await setNulled(args, id, i, false)
			break
		}
	}
}

export const useToggleLock = (id: GameId) => useRecoilCallback(args => (i: RosterPositionIndex) => toggleLock(args, id, i))

async function setLock(args: CallbackInterface, id: GameId, i: RosterPositionIndex, value: boolean) {

	const { set } = args

	set(locks(id), prevData => {
		const newLocks = prevData.slice()
		newLocks[i] = value
		return newLocks
	})
}

async function setNulled(args: CallbackInterface, id: GameId, i: RosterPositionIndex, value: boolean) {

	const { set, snapshot } = args

	const b = await snapshot.getPromise(best(id))
	const n = await snapshot.getPromise(myNumPeriods)
	const f = await snapshot.getPromise(myGameFormat)

	let nulled
	if (value) {
		//
		// Add to nulled
		//
		nulled = Array.from(new Set([
			...b.nulled,
			i,
		]))
	} else {
		//
		// Remove from nulled
		//
		nulled = b.nulled.filter(value => value !== i)
	}

	const newState = sanitizeBest({
		...b,
		nulled,
	}, n, f)

	set(best(id), newState)

	await addToHistory(args, newState)

	set(madeChanges(id), true)
}

const includeFutureRosters = atom({
	key: 'includeFutureRosters',
	default: false,
})

export const useIncludeFutureRostersState = () => useRecoilState(includeFutureRosters)

const includeFillins = atom({
	key: 'includeFillins',
	default: false,
})

export const useIncludeFillinsState = () => useRecoilState(includeFillins)

function keyToId(str: string) {

    const arr = str.split('__').map(bit => bit.replace(/"/g, ''))

    const name = arr.shift()

    // Make sure each component is valid
    const id = (arr.filter(bit => !bit || bit === 'null' || bit === 'undefined').length === 0 && arr.join('__')) || undefined

    return {
        name,
        id,
    }
}

//
// This is a bit slippery because it can be true when we have a user.
// At the moment I'm being careful in App.tsx and Login.tsx to update
// it whenever the user might login.
//
export type LoginMode = 'signIn' | 'createAccount'

const showLogin = atom<false | LoginMode>({
	key: 'showLogin',
	default: false,
})

//
// While Login.tsx is validating a provider/SMS auth result, it owns closing
// the login modal. App.tsx should still publish Firebase user state, but
// should not auto-dismiss the modal before Login can reject accidental new users.
//
const loginModalValidationInProgress = atom({
	key: 'loginModalValidationInProgress',
	default: false,
})

export const useSetShowLogin = () => useSetRecoilState(showLogin)
export const useShowLoginState = () => useRecoilState(showLogin)
export const useLoginModalValidationInProgress = () => useRecoilValue(loginModalValidationInProgress)
export const useSetLoginModalValidationInProgress = () => useSetRecoilState(loginModalValidationInProgress)

//
// Should we be showing the team/season selector popup?
//
const showTeamSelection = atom<boolean | null>({
	key: 'showTeamSelection',
	default: null,		// Not false because Teams.js wants to detect when we're starting up with no team selected
})

export const useShowTeamSelection = () => useRecoilValue(showTeamSelection)
export const useSetShowTeamSelection = () => useSetRecoilState(showTeamSelection)
export const useShowTeamSelectionState = () => useRecoilState(showTeamSelection)

const rosterIsStale = atomFamily<boolean, GameId>({
    key: 'rosterIsStale',
    default: false,
})

const myRosterIsStale = selector<boolean>({
    key: 'myRosterIsStale',
    get: ({ get }) => {
        const id = get(myGameId)
        return get(rosterIsStale(id || '')) || false
    },
    set: ({ get, set }, newValue) => {
        const id = get(myGameId)
        return set(rosterIsStale(id || ''), newValue)
    },
})

export const useMyRosterIsStale = () => useRecoilValue(myRosterIsStale)
export const useSetMyRosterIsStale = () => useSetRecoilState(myRosterIsStale)

const settings = atom<Settings>({
    key: 'settings',
    default: { },
	effects: [
		saveLocalSettings(),
    ],
})

export const useSettings = () => useRecoilValue(settings)
export const useSettingsState = () => useRecoilState(settings)

//
// updateSectionFromUrl
//
async function updateSectionFromUrl(args: CallbackInterface, pathname?: string, search?: string) {

    const { set, snapshot } = args

    const prevSection = await snapshot.getPromise(section)
    const newSection: Section = { }
    let teamOrSeasonChanged = false

    if (search) {
		const params = new URLSearchParams(search)
		const hasTeamParam = params.has('team')
		const hasSeasonParam = params.has('season')

		if (hasTeamParam) {
			const value = params.get('team') || undefined
			newSection.team = value
			if (value !== prevSection.team) {
				teamOrSeasonChanged = true
			}
		}

		if (hasSeasonParam) {
			const value = params.get('season') || undefined
			newSection.season = value
			if (value !== prevSection.season) {
				teamOrSeasonChanged = true
			}
		} else if (hasTeamParam) {
			newSection.season = undefined
			if (prevSection.season) {
				teamOrSeasonChanged = true
			}
		}
    }

    if (teamOrSeasonChanged) {
        newSection.game = undefined
        newSection.player = undefined
    }

    if (pathname) {
        const params = pathname.split('/').filter(v => v)
        if (params.length) {
            // Only change leaf IDs when the route names one. Ionic keeps outgoing
            // detail pages mounted during transitions, so clearing an ID on the
            // collection route would invalidate the page while it animates away.
            if (params[0] === 'games' && params[1]) {
                newSection.game = params[1]
                newSection.player = undefined
            } else if (params[0] === 'players' && params[1]) {
                newSection.player = params[1]
                newSection.game = undefined
            }
        }
    }

    if (Object.keys(newSection).length) {
        let changedSomething = false
        Object.keys(newSection).forEach(key => {
            if (newSection[key as keyof Section] !== prevSection[key as keyof Section]) {
                changedSomething = true
            }
        })
        if (changedSomething) {
            set(section, prevSection => ({
                ...prevSection,
                ...newSection
            }))
        }
    }
}

export const updateSectionFromUrlForTesting = updateSectionFromUrl

export const useUpdateSectionFromUrl = () => useRecoilCallback(args => (pathname?: string, search?: string) => updateSectionFromUrl(args, pathname, search), [ ])

const ready = atom({
    key: 'ready',
    default: true,
})

export const useReady = () => useRecoilValue(ready)
export const useSetReady = () => useSetRecoilState(ready)

//
// preferences - Roster position preferences for players in particular positions
//

const myPreferences = selector<PlayersPrefs>({
    key: 'myPreferences',
    get: ({ get }) => {
		const prefs: {
			[key: PlayerId]: PlayerPrefs | undefined
		} = { }
		const p = get(myPlayers)
		if (p) {
			Object.entries(p).forEach(([ playerId, player ]) => {
				prefs[playerId] = player.prefs
			})
		}
		return prefs
    },
})

export const useMyPreferences = () => useRecoilValue(myPreferences)

const showPositionPreferences = atom({
    key: 'showPositionPreferences',
    default: false,
})

export const useShowPositionPreferencesState = () => useRecoilState(showPositionPreferences)

//
// netStats
//
const myNetStats = selector({
    key: 'netStats',
    get: ({ get }) => {
        const g = get(myGames)
        const p = get(myPlayerNamesStringified)
		const players = p ? JSON.parse(p) as Players : undefined
		const n = get(myNumPeriods)
		const f = get(myGameFormat)
		const r = get(myRosters)
        return generateAllNetStats({
            games: g || undefined,
			rosters: r,
            players,
			numPeriods: n,
			gameFormat: f,
        })
    },
})

export const useMyNetStats = () => useRecoilValue(myNetStats)

//
// comboStats
//
const myComboStats = selector<ComboStats>({
    key: 'comboStats',
    get: ({ get }) => {
		const combo: ComboStats = {
			pairs: { },
			trios: { },
		}
		const g = get(myGames)
		const r = get(myOnCourtRosters)
		const n = get(myNumPeriods)
		const f = get(myGameFormat)
		if (g) {
			const tmp = {
				pairs: findPairings({
					games: g,
					onCourtRosters: r,
					numPeriods: n,
					gameFormat: f,
					findTrios: false,
					userHasPro: true,
				}),
				trios: findPairings({
					games: g,
					onCourtRosters: r,
					numPeriods: n,
					gameFormat: f,
					findTrios: true,
					userHasPro: true,
				})
			}

			PAIR_MODES.forEach(mode => {
				tmp[mode].forEach(row => {

					const { id, diff, n } = row

					let value = diff / n

					const norm = NORMALIZE_MIN_QTRS_FOR_PAIRS[mode]

					if (n < norm) {
						value *= 0.80 / Math.pow(norm - n, 1.5)
					}

					combo[mode] = {
						...combo[mode],
						[id]: value,
					}
				})
			})
		}
		return combo
	},
})

export const useMyComboStats = () => useRecoilValue(myComboStats)

const editMode = atom({
    key: 'editMode',
    default: false,
})

export const useEditMode = () => useRecoilValue(editMode)
export const useEditModeState = () => useRecoilState(editMode)

const activeTab = atom<string | null>({
    key: 'activeTab',
    default: null,
})

export const useActiveTab = () => useRecoilValue(activeTab)
export const useActiveTabState = () => useRecoilState(activeTab)

const amActiveTab = selectorFamily<boolean, string>({
    key: 'amActiveTab',
    get: id => ({ get }) => {
        const t = get(activeTab)
        return t === id
    },
})

export const useAmActiveTab = (id: string) => useRecoilValue(amActiveTab(id))

const currentPathname = atom<string>({
	key: 'currentPathname',
	default: typeof window === 'undefined' ? '/' : window.location.pathname,
})

export const useSetCurrentPathname = () => useSetRecoilState(currentPathname)

const newsNotification = atom({
	key: 'newsNotification',
	default: false,
})

export const useNewsNotification = () => useRecoilValue(newsNotification)
export const useNewsNotificationState = () => useRecoilState(newsNotification)
export const useSetNewsNotification = () => useSetRecoilState(newsNotification)

/*
 *
 * Selectors!
 *
 */


//
// showNetStats - if user has permission and hasn't toggled them off in settings
//
const showNetStats = selector({
    key: 'showNetStats',
    get: ({ get }) => {
        if (get(canViewNetStats)) {
            const p = get(settings)
            if (p?.showNetStats) {
                return true
            }
        }
        return false
    },
})

export const useShowNetStats = () => useRecoilValue(showNetStats)

//
// showNetStatsCombos - true if toggled on in Settings + 'showNetStats' is also true
//
const showNetStatsCombos = selector({
    key: 'showNetStatsCombos',
    get: ({ get }) => {
        if (get(showNetStats)) {
            const p = get(settings)
            if (p?.showNetStatsCombos) {
                return true
            }
        }
        return false
    },
})

export const useShowNetStatsCombos = () => useRecoilValue(showNetStatsCombos)

//
// true if logged-in user is viewing team they are allowed to manage
//
const amViewingOwnTeam = selector({
    key: 'amViewingOwnTeam',
    get: ({ get }) => {
        const u = get(user)
        if (u) {
            const t = get(myTeam)
            if (t?.managers?.includes(u.uid)) {
                return true
            }
        }
        return false
    },
})

export const useAmViewingOwnTeam = () => useRecoilValue(amViewingOwnTeam)

//
// true if user is a club admin for the current team
//
const amClubAdmin = selector({
	key: 'amClubAdmin',
	get: ({ get }) => {
		const u = get(user)
		if (u) {
			const c = get(club)
			if (c?.admin?.includes(u.uid)) {
				//
				// I'm a club admin, but is this team in the club?
				//
				const t = get(myTeam)
				if (t?.managers?.some(id => c?.users?.includes(id))) {
					// At least one of this team's managers is in the club,
					// so club admin can view club-scoped team data.
					return true
				}
			}
		}
		return false
	},
})

export const useAmClubAdmin = () => useRecoilValue(amClubAdmin)

//
// Can I view anything?
//
const canView = selector<boolean>({
	key: 'canView',
	get: ({ get  }) => get(amViewingOwnTeam) || !get(mySeason)?.privacy,
})

export const useCanView = () => useRecoilValue(canView)

//
// You see NetStats if you are:
// * a team manager; or
// * a club admin
//
const canViewNetStats = selector<boolean>({
    key: 'canViewNetStats',
    get: ({ get }) => get(amViewingOwnTeam) || get(amClubAdmin),
})

export const useCanViewNetStats = () => useRecoilValue(canViewNetStats)

//
// You can edit items if you're a team manager, or if you're viewing
// a list of your teams.
//
const canEditItem = selectorFamily<boolean, string | undefined>({
    key: 'canEditItem',
    get: itemUnit => ({ get }) => {

        if (get(amViewingOwnTeam)) {
            return true
        }

        //
        // User viewing "My Teams" page
        //

        const u = get(user)
		const amViewingMyTeams = get(showTeamSelection)
        if (u && amViewingMyTeams && (itemUnit === 'team' || itemUnit === 'season')) {
            return true
        }

        return false
    },
})

export const useCanEditItem = (itemType?: string) => useRecoilValue(canEditItem(itemType))

const canAddItem = selectorFamily({
    key: 'canAddItem',
    get: itemUnit => ({ get }) => {
        const u = get(user)

        if (u) {

			if (itemUnit === 'team') {
                //
                // Any user can create a team except free email+password only
				// accounts that haven't been verified.
                //
				if (get(userMustVerifyEmail)) {
					return false
				}

				return true
			}

			if (itemUnit === 'club') {
				//
				// Any user can create a club.
				//
				return true
			}

			if (itemUnit === 'season') {
				//
				// Only permit if we're viewing a team that we can manage
				//

				const t = get(myRelevantTeam)
				if (t?.managers?.includes(u.uid)) {
					return true
				}
			}

            if (get(amViewingOwnTeam)) {
                return true
            }
        }

        return false
    },
})

export const useCanAddItem = (itemUnit: string) => useRecoilValue(canAddItem(itemUnit))

const userMustVerifyEmail = selector({
	key: 'userMustVerifyEmail',
	get: ({ get }) => {
		const u = get(user)

		if (u) {
			//
			// If it's a paid account, we know it's not spam, so let
			// them add stuff.
			//
			if (get(userHasPro)) {
				return false
			}

			//
			// If this is an email + password account only (not an Apple or Google
			// account), they need to verify.
			//
			if (!u.emailVerified) {
				if (u.providerData?.length) {
					// When creating a new user on Android, the providerData array has
					// a 'firebase' entry for some reason
 					const myProviders = u.providerData.filter(p => p.providerId !== 'firebase')
 					if (myProviders.length === 1 && myProviders[0].providerId === 'password') {
 						return true
 					}
 				}
			}
		}

		return false
	},
})

export const useUserMustVerifyEmail = () => useRecoilValue(userMustVerifyEmail)

//
// Store stuff
//

const storeOfferings = atom<PurchasesOfferings | null>({
	key: 'storeOfferings',
	default: null,
})

export const useStoreOfferings = () => useRecoilValue(storeOfferings)
export const useSetStoreOfferings = () => useSetRecoilState(storeOfferings)

const storeOfferingsError = atom<string | null>({
	key: 'storeOfferingsError',
	default: null,
})

export const useStoreOfferingsError = () => useRecoilValue(storeOfferingsError)
export const useSetStoreOfferingsError = () => useSetRecoilState(storeOfferingsError)

const storeCustomerInfo = atomFamily<StoreCustomerInfo | null, UserId>({
	key: 'storeCustomerInfo',
	default: null,
})

export const useStoreCustomerInfo = (id: UserId) => useRecoilValue(storeCustomerInfo(id))
export const useSetStoreCustomerInfo = (id: UserId) => useSetRecoilState(storeCustomerInfo(id))

const storeCustomerInfoError = atomFamily<string | null, UserId>({
	key: 'storeCustomerInfoError',
	default: null,
})

export const useStoreCustomerInfoError = (id: UserId) => useRecoilValue(storeCustomerInfoError(id))
export const useSetStoreCustomerInfoError = (id: UserId) => useSetRecoilState(storeCustomerInfoError(id))

export const customerHasProHistory = (info: StoreCustomerInfo | null) => {
	if (!info) {
		return false
	}

	if (info.entitlements?.pro) {
		return true
	}

	return Object.values(info.subscriptions || { }).some(subscription =>
		subscription.store !== 'promotional'
	)
}

export const customerPlanEnd = (info: StoreCustomerInfo | null) => {
	if (!info?.subscriptions) {
		return null
	}

	const now = new Date()
	const activeSubscriptions = Object.values(info.subscriptions)
		.filter(subscription => new Date(subscription.expires_date) > now)
		.sort((a, b) => new Date(a.expires_date).getTime() - new Date(b.expires_date).getTime())

	const [ subscription ] = activeSubscriptions

	if (!subscription) {
		return null
	}

	return {
		date: new Date(subscription.expires_date),
		renews: !subscription.unsubscribe_detected_at && subscription.store !== 'promotional',
	}
}

export const shouldOfferFreeTrial = (info: StoreCustomerInfo | null, error?: string | null) => {
	if (!info || error) {
		return false
	}

	return !customerHasProHistory(info)
}

/*
const storeUserInfo = selector({
	key: 'storeUserInfo',
	get: ({ get }) => {
		const u = get(user)
		if (!u)
			return null
		return get(storeCustomerInfo(u.uid))
	}
})

export const useStoreUserInfo = () => useRecoilValue(storeUserInfo)
*/

const customerHasPro = selectorFamily<boolean, UserId>({
	key: 'customerHasPro',
	get: id => ({ get }) => {
		if (!id) {
			return false
		}

		return hasActiveProEntitlement(get(storeCustomerInfo(id)))
	},
})

const userHasProPersonally = selector<boolean>({
	key: 'userHasProPersonally',
	get: ({ get }) => {
		const u = get(user)
		if (!u)
			return false
		return get(customerHasPro(u.uid))
	}
})

export const useUserHasProPersonally = () => useRecoilValue(userHasProPersonally)

const clubHasPro = selector<boolean>({
	key: 'clubHasPro',
	get: ({ get }) => {
		const c = get(club)
		if (!c)
			return false
		return get(customerHasPro(c.id))
	}
})

export const useClubHasPro = () => useRecoilValue(clubHasPro)

//
// True if they have it personally or via club
//
const userHasPro = selector({
    key: 'userHasPro',
	get: ({ get }) => {
		return get(userHasProPersonally) || get(clubHasPro)
	}
})

export const useUserHasPro = () => useRecoilValue(userHasPro)

export const useQueryMyTeamHasPro = () => useRecoilCallback(({ snapshot }) => async () => {
	const currentTeam = await snapshot.getPromise(myTeam)
	if (!currentTeam) {
		return false
	}

	const result = await checkTeamFlowEntitlement({ teamId: currentTeam.id })
	return result.data.hasPro
}, [ ])

const showIntroScreenForPath = selectorFamily<boolean, string>({
	key: 'showIntroScreen',
	get: pathname => ({ get }) => {
		if (get(user)) {
			return false
		}
		if (pathname && pathname !== '/') {
			return false
		}
		const s = get(section)
		if (Object.keys(s).length) {
			return false
		}
		return true
	},
})

const showIntroScreen = selector<boolean>({
	key: 'showIntroScreenCurrentPath',
	get: ({ get }) => get(showIntroScreenForPath(get(currentPathname))),
})

export const useShowIntroScreen = () => useRecoilValue(showIntroScreen)
export const useShowIntroScreenForPath = (pathname: string) => useRecoilValue(showIntroScreenForPath(pathname))

//
// metadata
//
// For Firebase
//

type MetadataType = {
	isCollection: boolean
	cid: string
	docId?: string
}

type MetadataTypes = 'teams' | 'userTeams' | 'userClubs' | 'clubs' | 'clubUser' | 'teamSeasons' | 'teamGames' | 'teamPlayers' | 'teamRosters' | 'teamTrainingSessions' | 'trainingNote' | 'team' | 'season' | 'game' | 'player' | 'roster' | 'flow' | 'training'


const metadata = ({ name, id }: { name: string, id: string }) => {

	if (!name) {
		console.error("Required parameter missing", name)
	}

	const [ teamId, seasonId ] = id.split(/__/)

	const types: {
		[key in MetadataTypes]: MetadataType
	} = {
		'teams': {
			isCollection: true,
			cid: `/teams`,
		},
		'userTeams': {
			isCollection: true,
			cid: `/teams`,
		},
		'userClubs': {
			isCollection: true,
			cid: `/clubs`,
		},
		'clubs': {
			isCollection: true,
			cid: `/clubs`,
		},
		'clubUser': {
			isCollection: false,
			cid: `/clubs/${teamId}/userInfo`,			// teamId is actually clubId
			docId: seasonId,							// docId is actually userId
		},
		'teamSeasons': {
			isCollection: true,
			cid: `/teams/${teamId}/seasons`,
		},
		'teamGames': {
			isCollection: true,
			cid: `/teams/${teamId}/seasons/${seasonId}/games`,
		},
		'teamPlayers': {
			isCollection: true,
			cid: `/teams/${teamId}/seasons/${seasonId}/players`,
		},
		'teamRosters': {
			isCollection: true,
			cid: `/teams/${teamId}/seasons/${seasonId}/rosters`,
		},
		'teamTrainingSessions': {
			isCollection: true,
			cid: `/teams/${teamId}/seasons/${seasonId}/training`,
		},
		'trainingNote': {
			isCollection: false,
			cid: `/teams/${teamId}/notes`,
			docId: seasonId,
		},

		'team': {
			isCollection: false,
			cid: `/teams`,
		},
		'season': {
			isCollection: false,
			cid: `/teams/${id}/seasons`,
		},
		'game': {
			isCollection: false,
			cid: `/teams/${teamId}/seasons/${seasonId}/games`,
		},
		'player': {
			isCollection: false,
			cid: `/teams/${teamId}/seasons/${seasonId}/players`,
		},
		'roster': {
			isCollection: false,
			cid: `/teams/${teamId}/seasons/${seasonId}/rosters`,
		},
		'flow': {
			isCollection: false,
			cid: `/teams/${teamId}/seasons/${seasonId}/flows`,
		},
		'training': {
			isCollection: false,
			cid: `/teams/${teamId}/seasons/${seasonId}/training`,
		},
	}

    //
    // Collections: 'teams', 'seasons', 'games', 'players', 'rosters'
    //
	const value = types[name as keyof typeof types]

    if (!value) {
        throw new Error('Unknown metadata type: ' + name)
    }

    return value
}

export function queryDbid(name: keyof typeof dbids, section: Section) {
	const dbid = dbids[name]
    return dbid.replace(/__(\w+)__/g, (_, p1) => {
        const field = p1.toLowerCase() as keyof Section
        return section[field] || '??'
    })
}

/*
export function typeToDbid(type: { dbid: string }, section: Section) {
    return type.dbid.replace(/__(\w+)__/g, (_, p1) => {
        const field = p1.toLowerCase() as keyof Section
        return section[field] || '??'
    })
}

// Shortcut for the above.
// Uses some naughty duplication of 'dbid' field from Players.jsx
export function playerDbid(section: Section) {
	const type = {
		dbid: `/teams/__TEAM__/seasons/__SEASON__/players`,
	}
	return typeToDbid(type, section)
}
*/

/*
 * functions
 */

type DeleteItemProps<T> = {
	type: ItemTypeDefinition<T>
	item: T
	onError?: (err: unknown) => void
	allowDifferentContext?: boolean
	onConfirmDifferentContext?: () => boolean | void | Promise<boolean | void>
	onViewSeason?: () => boolean | void | Promise<boolean | void>
	onViewTeam?: () => boolean | void | Promise<boolean | void>
	parentName?: string
	ownedByTeamId?: TeamId
}

const deleteItem = async <T extends Item>(args: CallbackInterface, props: DeleteItemProps<T>) => {

	const { snapshot } = args

	const { type, item } = props

	if (type.unit === 'coach') {
		throw new Error("Cannot delete a coach.")
	}
	const deletableUnit = type.unit

	const header = ('name' in item && item.name) || `Delete ${ucFirst(type.unit)}`

	const sec = await snapshot.getPromise(section)

	let hasRoster: boolean | string = false
	const rosters = await snapshot.getPromise(myRosters)

	if (type.unit === 'game') {
		hasRoster = !!rosters?.[item.id]?.roster?.length
	} else if (type.unit === 'player') {
		if (rosters) {
			const numPeriods = await snapshot.getPromise(myNumPeriods)
			const gameFormat = await snapshot.getPromise(myGameFormat)
			const rosterWithPositions = Object.values(rosters).find(r => {
				const myPositions = findMyPositions({
					playerId: item.id,
					roster: r.roster,
					endRoster: r.endRoster,
					created: r.created,
					endRosterCreated: r.endRosterCreated,
					players: r.players,
					nulled: r.nulled,
					subs: r.subs,
					numPeriods,
					gameFormat,
				})
				const ret = myPositions.some(obj => obj !== ROSTER_NOT_AVAILABLE && obj.positionName !== BENCHED && !obj.subStatus)
				return ret
			})
			hasRoster = !!rosterWithPositions
			if (rosterWithPositions?.id) {
				//
				// Provide some text on which roster it is, because it's confusing otherwise.
				//
				const games = await snapshot.getPromise(myGames)
				const g = games?.[rosterWithPositions.id]
				if (g) {
					hasRoster = `Round ${g.round} vs ${g.opponent} - ${new Date(g.date).toLocaleDateString('en-AU')}`
				}
			}
		}
	}

	if (type.unit === 'season') {
		if (!props.ownedByTeamId) {
			throw new Error(`Cannot delete season ${item.id}: owning team ID is missing.`)
		}
		const targetSeasonId = item.id
		const targetSeasonCollection = `/teams/${props.ownedByTeamId}/seasons`

		const { season } = await snapshot.getPromise(section)
		if (season !== targetSeasonId && !props.allowDifferentContext) {
			const teamName = props.parentName ? ` for ${props.parentName}` : ''
			return {
				header: 'Delete a Different Season?',
				subHeader: header,
				message: `You are about to delete "${header}"${teamName}. This is NOT the season you are currently viewing. Continue only if you're sure this is the season you intend to delete.`,
				buttons: [
					{
						text: 'Cancel',
						role: 'cancel',
					},
					{
						text: 'View season',
						handler: props.onViewSeason,
					},
					{
						text: 'Continue',
						role: 'destructive',
						handler: props.onConfirmDifferentContext,
					},
				],
			}
		}

		return {
			header: props.parentName ? `${props.parentName}: ${header}` : header,
			message: "Deleting a season will remove all of its games, rosters, recorded flow data, players, and training data. THIS IS PERMANENT and cannot be undone. Are you sure?",
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Delete season',
					role: 'destructive',
					handler: async () => {
						try {
							//
							// Delete rosters/, games/, flows/, players/, training/
							//
							// Any collections we add in the future should be added here too.
							//
							const collectionIds = [ 'rosters', 'games', 'flows', 'players', 'training' ].map(collectionName => (
								targetSeasonCollection + '/' + targetSeasonId + '/' + collectionName
							))
							await deleteCollectionsAndDocumentsInBatch(collectionIds, [
								{
									colId: targetSeasonCollection,
									docId: targetSeasonId,
								},
							])
						} catch (err) {
							if (props.onError) {
								props.onError(err)
							} else {
								console.error("Failed to delete season", err)
							}
						}
					},
				},
			],
		}

	} else if (type.unit === 'team') {
		const targetTeamId = item.id

		const { team } = await snapshot.getPromise(section)
		// check whether this team has any seasons. If it does, say you have to delete the seasons first.
		const collectionRef = collection(db, `/teams/${targetTeamId}/seasons`)
		const q = query(collectionRef)
		const data = await getDocs(q)
		if (data.size > 0) {
			return {
				header: 'Team Has Existing Season',
				subHeader: header,
				message: "Before you can delete a team, you must first delete its season(s).",
				buttons: [ 'OK' ],
			}
		}

		const deleteTeam = () => deleteCollectionsAndDocumentsInBatch(
			[ `/teams/${targetTeamId}/notes` ],
			[
				{
					colId: '/teams',
					docId: targetTeamId,
				},
			],
		)

		if (team !== targetTeamId) {
			return {
				header: 'Delete a Different Team?',
				subHeader: header,
				message: `You are about to delete ${header}, which is not the team you are currently viewing. THIS IS PERMANENT and cannot be undone. Are you absolutely sure?`,
				buttons: [
					{
						text: 'Cancel',
						role: 'cancel',
					},
					{
						text: 'View team',
						handler: props.onViewTeam,
					},
					{
						text: 'Delete team',
						role: 'destructive',
						handler: async () => {
							try {
								await deleteTeam()
							} catch (err) {
								if (props.onError) {
									props.onError(err)
								} else {
									console.error("Failed to delete team", err)
								}
							}
						},
					},
				],
			}
		}

		await deleteTeam()
		return {
			header: 'Team deleted',
			buttons: [ 'OK' ],
		}
	} else if (type.unit === 'player' && hasRoster) {
		const extraMessage = typeof hasRoster === 'string' ? `(${hasRoster})` : ''
		return {
			header,
			message: "You can't delete this player because they are in an on-court position in at least one roster. " + extraMessage,
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
			],
		}
	} else {
		let message = `Are you sure you want to permanently delete this ${type.unit}?`
		if (type.unit === 'game') {
			message += hasRoster
				? " This will also permanently remove its roster and any recorded flow data."
				: " This will also permanently remove any recorded flow data."
		} else if (hasRoster && type.unit === 'player') {
			message += " This will distort any rosters in which they are included."
		}
		return {
			header,
			message,
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Delete',
					role: 'destructive',
					handler: async () => {
						try {
							const dbid = queryDbid(deletableUnit, sec)
							const documentsToDelete = [
								{
									colId: dbid,
									docId: item.id,
								},
							]
							if (hasRoster && type.unit === 'game') {
								const colId = queryDbid('roster', sec)
								documentsToDelete.push({
									colId,
									docId: item.id,
								})
							}
							if (type.unit === 'game') {
								const colId = queryDbid('flow', sec)
								documentsToDelete.push({
									colId,
									docId: item.id,
								})
							}
							await deleteDocumentsInBatch(documentsToDelete)
						} catch (err) {
							if (props.onError) {
								props.onError(err)
							} else {
								console.error("Failed to delete item", err)
							}
						}
					},
				},
			],
		}
	}
}

export const useDeleteItem = <T extends Item>() => useRecoilCallback(args => (props: DeleteItemProps<T>) => deleteItem(args, props))

// Used by /tests/delete-item.test.tsx
export const deleteItemForTesting = deleteItem

type DeleteDocumentProps = {
	colId: string
	docId: string
}

function queueDeleteDocument(batch: ReturnType<typeof writeBatch>, props: DeleteDocumentProps) {
	console.log("Deleting", props)
	batch.delete(doc(db, props.colId, props.docId))
}

async function getCollectionDocumentRefs(colId: string) {
	const collectionRef = collection(db, colId)
	const q = query(collectionRef)
	const data = await getDocs(q)
	return data.docs.map(docSnapshot => docSnapshot.ref)
}

//
// Tiny risk of falling afoul of Firestore’s 500-write batch limit, but
// that is deemed highly unlikely given a typical season of 10 players +
// 20 games + 20 rosters + 20 flows + 20 training sessions.
//
async function deleteDocumentsInBatch(documents: DeleteDocumentProps[]) {
	await deleteCollectionsAndDocumentsInBatch([ ], documents)
}

async function deleteCollectionsAndDocumentsInBatch(collectionIds: string[], documents: DeleteDocumentProps[]) {
	const collectionDocumentRefs = (await Promise.all(collectionIds.map(getCollectionDocumentRefs))).flat()
	const batch = writeBatch(db)

	collectionDocumentRefs.forEach(documentRef => {
		console.log("Deleting", {
			colId: documentRef.parent.path,
			docId: documentRef.id,
		})
		batch.delete(documentRef)
	})

	documents.forEach(documentProps => queueDeleteDocument(batch, documentProps))

	await batch.commit()
}
